import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { toast } from 'sonner';

import {
  ArrowSquareOut,
  BookOpen,
  Check,
  CircleNotch,
  Copy,
  DownloadSimple,
  Eye,
  FileText,
  FolderOpen,
  MagnifyingGlass
} from '@phosphor-icons/react';
const AdminDocumentation = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewContent, setPreviewContent] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/docs/list`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents);
      } else {
        toast.error('Failed to load documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (doc) => {
    setSelectedDoc(doc);
    setPreviewLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/docs/view/${doc.id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const html = await response.text();
        setPreviewContent(html);
      } else {
        toast.error('Failed to load preview');
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/docs/download/${doc.id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`Downloaded ${doc.filename}`);
      } else {
        toast.error('Failed to download document');
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Failed to download document');
    }
  };

  const handleCopyLink = (doc) => {
    const link = `${BACKEND_URL}/api/admin/docs/view/${doc.id}`;
    navigator.clipboard.writeText(link);
    setCopiedId(doc.id);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleOpenInNewTab = (doc) => {
    window.open(`${BACKEND_URL}/api/admin/docs/view/${doc.id}`, '_blank');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredDocs = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group by category
  const groupedDocs = filteredDocs.reduce((acc, doc) => {
    if (!acc[doc.category]) {
      acc[doc.category] = [];
    }
    acc[doc.category].push(doc);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0A0F1A] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
              <BookOpen size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Documentation Library</h1>
              <p className="text-sm text-gray-400">View, download, and share documentation with customers</p>
            </div>
          </div>
        </div>

        {/* MagnifyingGlass */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-[#1A1B1E] border border-[rgba(255,255,255,0.1)] rounded-xl text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch className="w-8 h-8 animate-spin text-[#6366F1]" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Documents Yet</h3>
            <p className="text-gray-400">Documentation will appear here once created.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Document List */}
            <div className="space-y-6">
              {Object.entries(groupedDocs).map(([category, docs]) => (
                <div key={category}>
                  <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <FileText size={18} className="text-[#6366F1]" />
                    {category}
                  </h2>
                  <div className="space-y-3">
                    {docs.map((doc) => (
                      <div
                        key={doc.id}
                        className={`p-4 rounded-xl border transition-all cursor-pointer ${
                          selectedDoc?.id === doc.id
                            ? 'bg-[#6366F1]/10 border-[#6366F1]'
                            : 'bg-[#1A1B1E] border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
                        }`}
                        onClick={() => handlePreview(doc)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-medium truncate">{doc.title}</h3>
                            <p className="text-sm text-gray-400 mt-1 line-clamp-2">{doc.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span>{doc.filename}</span>
                              <span>{formatFileSize(doc.file_size)}</span>
                              <span>Updated: {doc.updated_at}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[rgba(255,255,255,0.05)]">
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePreview(doc); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[rgba(255,255,255,0.05)] text-gray-300 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                          >
                            <Eye size={14} />
                            Preview
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#6366F1]/20 text-[#6366F1] rounded-lg hover:bg-[#6366F1]/30 transition-colors"
                          >
                            <DownloadSimple size={14} />
                            DownloadSimple
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCopyLink(doc); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[rgba(255,255,255,0.05)] text-gray-300 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                          >
                            {copiedId === doc.id ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                            {copiedId === doc.id ? 'Copied!' : 'Copy Link'}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleOpenInNewTab(doc); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[rgba(255,255,255,0.05)] text-gray-300 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                          >
                            <ArrowSquareOut size={14} />
                            Open
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Preview Panel */}
            <div className="lg:sticky lg:top-6 h-fit">
              <div className="bg-[#1A1B1E] rounded-xl border border-[rgba(255,255,255,0.1)] overflow-hidden">
                <div className="p-4 border-b border-[rgba(255,255,255,0.1)] flex items-center justify-between">
                  <h3 className="text-white font-medium">
                    {selectedDoc ? selectedDoc.title : 'Select a document to preview'}
                  </h3>
                  {selectedDoc && (
                    <button
                      onClick={() => handleOpenInNewTab(selectedDoc)}
                      className="text-[#6366F1] hover:text-[#818CF8] transition-colors"
                      title="Open in new tab"
                    >
                      <ArrowSquareOut size={18} />
                    </button>
                  )}
                </div>
                
                <div className="h-[600px] overflow-auto bg-white">
                  {previewLoading ? (
                    <div className="flex items-center justify-center h-full bg-[#0A0F1A]">
                      <CircleNotch className="w-8 h-8 animate-spin text-[#6366F1]" />
                    </div>
                  ) : selectedDoc ? (
                    <iframe
                      srcDoc={previewContent}
                      title="Document Preview"
                      className="w-full h-full border-0"
                      sandbox="allow-same-origin"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full bg-[#0A0F1A]">
                      <div className="text-center">
                        <Eye className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400">Click a document to preview</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDocumentation;
